import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronRight, Check } from 'lucide-react';
import ValuationForm from '../../components/ValuationForm';

const steps = [
  {
    num: '01',
    title: 'Kontaktaufnahme & Erstgespräch',
    description: 'Erzählen Sie mir von Ihrer Immobilie. In einem ersten Gespräch klären wir Ihre Ziele und ich beantworte Ihre Fragen.'
  },
  {
    num: '02',
    title: 'Objektbesichtigung',
    description: 'Ich besichtige Ihre Immobilie vor Ort und erfasse alle wertrelevanten Merkmale: Lage, Zustand, Ausstattung und Besonderheiten.'
  },
  {
    num: '03',
    title: 'Marktanalyse & Bewertung',
    description: 'Auf Basis aktueller Marktdaten, vergleichbarer Transaktionen und meiner Erfahrung erstelle ich eine fundierte Werteinschätzung.'
  },
  {
    num: '04',
    title: 'Ergebnispräsentation & Beratung',
    description: 'Wir besprechen das Ergebnis persönlich. Ich erkläre alle Faktoren und berate Sie zu den nächsten Schritten.'
  }
];

export default function Wertermittlung() {
  return (
    <div data-testid="wertermittlung-page">
      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-[#0a1120]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center gap-2 text-sm mb-8">
            <Link to="/" className="text-[#c8c6c4] hover:text-[#d1af6a]">Home</Link>
            <ChevronRight className="w-4 h-4 text-[#c8c6c4]" />
            <Link to="/leistungen" className="text-[#c8c6c4] hover:text-[#d1af6a]">Leistungen</Link>
            <ChevronRight className="w-4 h-4 text-[#c8c6c4]" />
            <span className="text-[#d1af6a]">Wertermittlung</span>
          </nav>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="font-accent text-[#d1af6a] text-xs tracking-[0.2em]">
              WERTERMITTLUNG
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white mt-4 mb-6">
              Professionelle Immobilien­wertermittlung
            </h1>
            <p className="text-[#c8c6c4] text-lg leading-relaxed">
              Eine fundierte Wertermittlung ist die Basis für jede erfolgreiche Immobilienentscheidung. 
              Erfahren Sie, was Ihre Immobilie wirklich wert ist – kostenlos und unverbindlich.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What is Wertermittlung */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-display text-2xl sm:text-3xl text-white mb-6">
                Was ist eine Wertermittlung?
              </h2>
              <p className="text-[#c8c6c4] leading-relaxed mb-4">
                Bei einer Immobilienwertermittlung wird der aktuelle Marktwert Ihrer Immobilie 
                ermittelt. Dabei berücksichtige ich zahlreiche Faktoren: Lage, Zustand, 
                Ausstattung, Energieeffizienz und aktuelle Marktentwicklungen.
              </p>
              <p className="text-[#c8c6c4] leading-relaxed mb-6">
                Das Ergebnis gibt Ihnen eine realistische Einschätzung, welcher Preis am 
                Markt erzielbar ist – ob Sie verkaufen, vermieten oder einfach nur den 
                Wert Ihres Eigentums kennen möchten.
              </p>
              <ul className="space-y-3">
                {['Kostenlose Ersteinschätzung', 'Fundierte Marktanalyse', 'Persönliche Beratung', 'Diskrete Abwicklung'].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-white">
                    <Check className="w-5 h-5 text-[#d1af6a]" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="card-luxury p-8">
              <h3 className="font-subheading text-xl text-white mb-4">
                Warum ist eine Wertermittlung wichtig?
              </h3>
              <ul className="space-y-4 text-[#c8c6c4]">
                <li>
                  <strong className="text-white">Beim Verkauf:</strong> Den optimalen Angebotspreis finden, 
                  der Käufer anzieht und den maximalen Erlös sichert.
                </li>
                <li>
                  <strong className="text-white">Bei der Vermietung:</strong> Eine marktgerechte Miete festlegen, 
                  die Leerstände vermeidet.
                </li>
                <li>
                  <strong className="text-white">Für die Finanzierung:</strong> Banken und Investoren überzeugen 
                  mit einer fundierten Wertaussage.
                </li>
                <li>
                  <strong className="text-white">Zur Vermögensübersicht:</strong> Den aktuellen Wert Ihres 
                  Eigentums kennen – für Erbschaft, Scheidung oder Steuerplanung.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 bg-[#1a2640]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="font-accent text-[#d1af6a] text-xs tracking-[0.2em]">
              DER ABLAUF
            </span>
            <h2 className="font-display text-3xl sm:text-4xl text-white mt-4">
              In 4 Schritten zur Wertermittlung
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <motion.div
                key={step.num}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="relative"
              >
                <span className="font-display text-6xl text-[#d1af6a]/20 absolute -top-4 -left-2">
                  {step.num}
                </span>
                <div className="pt-12">
                  <h3 className="font-subheading text-lg text-white mb-3">
                    {step.title}
                  </h3>
                  <p className="text-[#c8c6c4] text-sm leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="py-24 bg-[#0a1120]" id="formular">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="border border-[#d1af6a]/30 p-8 md:p-12">
            <div className="text-center mb-10">
              <div className="gold-divider mb-6">
                <div className="diamond" />
              </div>
              <h2 className="font-display text-3xl sm:text-4xl text-white mb-4">
                Jetzt kostenlose Bewertung anfordern
              </h2>
              <p className="text-[#c8c6c4]">
                Füllen Sie das Formular aus – ich melde mich zeitnah bei Ihnen.
              </p>
            </div>
            <ValuationForm variant="full" />
          </div>
        </div>
      </section>
    </div>
  );
}
